/**
 * Wave 14 Agent TRIAL - Layer 3.
 *
 * Always-on banner pinned to the top of the trial-tenant chrome. Shows
 * remaining days + a "extend by 7" tooltip when at least one milestone
 * is unachieved. Dismissible per-session via sessionStorage.
 */
import { useState } from 'react'
import { useTrialStatus } from '@/hooks/useTrialStatus'

const SESSION_DISMISS_KEY = 'wave14_trial_banner_dismissed'

export function TrialCountdownBanner() {
  const { status } = useTrialStatus()
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })

  if (!status || !status.in_trial || dismissed) {
    return null
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1')
    } catch {
      /* noop */
    }
    setDismissed(true)
  }

  const urgent = status.days_remaining <= 3
  const remaining = status.milestones.remaining.length
  const tone = urgent
    ? 'bg-rose-600 text-white'
    : 'bg-amber-500 text-amber-950'

  return (
    <div
      data-testid="trial-countdown-banner"
      className={`w-full px-4 py-2 flex items-center justify-between gap-4 text-sm font-medium ${tone}`}
    >
      <div>
        Trial: <strong>{status.days_remaining} days left</strong>
        {status.extension_days_granted_total > 0 && (
          <span className="opacity-80">
            {' '}
            (+{status.extension_days_granted_total} earned)
          </span>
        )}
        {remaining > 0 && (
          <span className="ml-3 opacity-90">
            Unlock {remaining} more milestone{remaining === 1 ? '' : 's'} for +7 days each.
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <a
          href="/pilot/apply"
          className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 underline-offset-2"
        >
          Upgrade to pilot
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss banner"
          className="opacity-80 hover:opacity-100"
        >
          ×
        </button>
      </div>
    </div>
  )
}

export default TrialCountdownBanner
